import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Subscription, timer } from 'rxjs';
import { CommonPaginationData } from 'src/app/components/common/common-pagination/common-pagination.component.model';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import {
  MigrationProcessing,
  MigrationProcessingSearchRequest,
  ScheduledTaskQueueItem
} from 'src/app/models';
import { DataService, WorkflowContextService } from 'src/app/services';
import { SettingsMigrateWorkflowActionCellComponent } from '../settings-migrate-workflow-action-cell/settings-migrate-workflow-action-cell.component';
import { getClientIdFromRoute } from '../settings-migrate-workflow.models';

interface MigrationProcessingRow {
  id: string;
  sourceSystem: string;
  jurisdiction: string;
  ownerName: string;
  createdBy: string;
  createdOnDisplay: string;
  startedOnDisplay: string;
  raw: MigrationProcessing;
}

type MigrationTaskStatus =
  | 'Queued'
  | 'Processing'
  | 'Completed'
  | 'Error'
  | 'Not Started';

interface MigrationProcessingMonitorRow {
  id: string;
  name: string;
  jurisdiction: string;
  ownerName: string;
  workflowStatus: MigrationTaskStatus;
  applicationStatus: MigrationTaskStatus;
  totalApplications: number;
  completedApplications: number;
  runningApplications: number;
  queuedApplications: number;
  failedApplications: number;
  progressPercent: number;
  queuedOn: string;
  executedOn: string;
  completedOn: string;
  queuedDuration: string;
  executionDuration: string;
  latestError: string;
}

@Component({
  selector: 'app-settings-migrate-workflow-list',
  standalone: false,
  templateUrl: './settings-migrate-workflow-list.component.html',
  styleUrls: ['./settings-migrate-workflow-list.component.scss']
})
export class SettingsMigrateWorkflowListComponent implements OnInit, OnDestroy {
  clientId = '';
  rows: MigrationProcessingRow[] = [];
  columns: TableColumn[] = [];
  isLoading = true;
  monitorLoading = false;
  monitorErrorMessage = '';
  errorMessage = '';
  successMessage = '';
  monitorExpanded = false;
  showCompletedOlderThanWeek = false;
  monitorCurrentPage = 0;
  monitorPageSize = 10;
  readonly monitorPageSizeOptions = [10, 25, 50];
  monitorProcessingRows: MigrationProcessingRow[] = [];
  monitorRecordCount = 0;
  monitorPageCount = 0;
  taskQueueItemsByProcessingId = new Map<string, ScheduledTaskQueueItem[]>();

  // Search properties
  searchTerm = '';

  private monitorPollingSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    this.clientId = getClientIdFromRoute(this.route, this.context) || '';
    this.columns = this.buildColumns();

    if (!this.clientId) {
      this.errorMessage = 'Unable to determine the current jurisdiction.';
      this.isLoading = false;
      return;
    }

    this.loadMigrationProcessings();
  }

  ngOnDestroy(): void {
    this.monitorPollingSub?.unsubscribe();
  }

  get monitorRows(): MigrationProcessingMonitorRow[] {
    return this.monitorProcessingRows.map(row => this.mapMonitorRow(row));
  }

  get monitorPaginationData(): CommonPaginationData {
    return {
      variant: 'staff',
      totalPages: this.monitorPageCount,
      currentPage: this.monitorCurrentPage,
      pageSize: this.monitorPageSize,
      pageSizeOptions: this.monitorPageSizeOptions,
      showRowsPerPage: true,
      showTotalRecords: true,
      visiblePages: this.getVisibleMonitorPages(),
      canGoPrevious: this.monitorCurrentPage > 0,
      canGoNext:
        this.monitorPageCount > 0 &&
        this.monitorCurrentPage < this.monitorPageCount - 1,
      totalRecords: this.monitorRecordCount
    };
  }

  get runningWorkflowCount(): number {
    return this.monitorRows.filter(row => row.workflowStatus === 'Processing')
      .length;
  }

  get queuedWorkflowCount(): number {
    return this.monitorRows.filter(row => row.workflowStatus === 'Queued')
      .length;
  }

  get completedWorkflowCount(): number {
    return this.monitorRows.filter(row => row.workflowStatus === 'Completed')
      .length;
  }

  get totalApplicationCount(): number {
    return this.monitorRows.reduce(
      (sum, row) => sum + row.totalApplications,
      0
    );
  }

  get completedApplicationCount(): number {
    return this.monitorRows.reduce(
      (sum, row) => sum + row.completedApplications,
      0
    );
  }

  get runningApplicationCount(): number {
    return this.monitorRows.reduce(
      (sum, row) => sum + row.runningApplications,
      0
    );
  }

  get failedApplicationCount(): number {
    return this.monitorRows.reduce(
      (sum, row) => sum + row.failedApplications,
      0
    );
  }

  loadMigrationProcessings(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const searchRequest = new MigrationProcessingSearchRequest({
      clientId: this.clientId,
      searchTerm: this.searchTerm || undefined,
      includeDeleted: false
    });

    this.dataService.searchMigrationProcessings(searchRequest).subscribe({
      next: processings => {
        this.rows = processings.map(processing => this.mapRow(processing));
        this.monitorCurrentPage = 0;
        this.isLoading = false;
        this.loadMonitorProcessings();
        this.startMonitorPolling();
      },
      error: err => {
        this.errorMessage =
          'Failed to load workflow migrations: ' +
          (err.error?.message || err.message);
        this.isLoading = false;
      }
    });
  }

  search(): void {
    this.loadMigrationProcessings();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.loadMigrationProcessings();
  }

  openCreate(): void {
    this.router.navigate(['create'], { relativeTo: this.route });
  }

  openDetail(processingId: string): void {
    this.router.navigate([processingId], { relativeTo: this.route });
  }

  toggleMonitor(): void {
    this.monitorExpanded = !this.monitorExpanded;
  }

  toggleOlderCompleted(): void {
    this.showCompletedOlderThanWeek = !this.showCompletedOlderThanWeek;
    this.monitorCurrentPage = 0;
    this.loadMonitorProcessings();
  }

  onMonitorPageChange(page: number): void {
    this.monitorCurrentPage = page;
    this.loadMonitorProcessings();
  }

  onMonitorPageSizeChange(size: number): void {
    this.monitorPageSize = Number(size) || 10;
    this.monitorCurrentPage = 0;
    this.loadMonitorProcessings();
  }

  goToFirstMonitorPage(): void {
    this.onMonitorPageChange(0);
  }

  goToPreviousMonitorPage(): void {
    if (this.monitorCurrentPage > 0) {
      this.onMonitorPageChange(this.monitorCurrentPage - 1);
    }
  }

  goToNextMonitorPage(): void {
    if (this.monitorCurrentPage < this.monitorPageCount - 1) {
      this.onMonitorPageChange(this.monitorCurrentPage + 1);
    }
  }

  goToLastMonitorPage(): void {
    if (this.monitorPageCount > 0) {
      this.onMonitorPageChange(this.monitorPageCount - 1);
    }
  }

  loadMonitorProcessings(showLoading = true): void {
    if (showLoading) {
      this.monitorLoading = true;
    }

    const request = new MigrationProcessingSearchRequest({
      clientId: this.clientId,
      searchTerm: this.searchTerm || undefined,
      includeDeleted: false,
      pageNumber: this.monitorCurrentPage,
      pageSize: this.monitorPageSize,
      includeCompletedOlderThanWeek: this.showCompletedOlderThanWeek
    });

    this.dataService.searchMigrationProcessingsPaged(request).subscribe({
      next: response => {
        this.monitorProcessingRows = (response.migrationProcessings || []).map(
          processing => this.mapRow(processing)
        );
        this.monitorRecordCount = response.recordCount || 0;
        this.monitorPageCount = response.pageCount || 0;
        this.monitorCurrentPage = response.pageNumber || 0;
        this.loadMonitorStatus(showLoading);
      },
      error: err => {
        this.monitorErrorMessage =
          'Failed to load migration monitor records: ' +
          (err.error?.message || err.message);
        this.monitorLoading = false;
      }
    });
  }

  loadMonitorStatus(showLoading = true): void {
    if (this.monitorProcessingRows.length === 0) {
      this.taskQueueItemsByProcessingId.clear();
      this.monitorLoading = false;
      return;
    }

    if (showLoading) {
      this.monitorLoading = true;
    }

    const requests = this.monitorProcessingRows.map(row =>
      this.dataService.getMigrationScheduledTaskQueueItems(row.id)
    );

    forkJoin(
      requests.length ? requests : [of([] as ScheduledTaskQueueItem[])]
    ).subscribe({
      next: results => {
        const nextMap = new Map<string, ScheduledTaskQueueItem[]>();
        this.monitorProcessingRows.forEach((row, index) => {
          nextMap.set(row.id, results[index] || []);
        });

        this.taskQueueItemsByProcessingId = nextMap;
        this.monitorErrorMessage = '';
        this.monitorLoading = false;
      },
      error: err => {
        this.monitorErrorMessage =
          'Failed to load migration task status: ' +
          (err.error?.message || err.message);
        this.monitorLoading = false;
      }
    });
  }

  handleAction(event: { action: 'open'; row: { id: string } }): void {
    this.openDetail(event.row.id);
  }

  getStatusClass(status: MigrationTaskStatus): string {
    return status.toLowerCase().replace(' ', '-');
  }

  private getVisibleMonitorPages(): (number | 'dots')[] {
    const totalPages = this.monitorPageCount;
    if (totalPages <= 1) {
      return [];
    }

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index);
    }

    const pages: (number | 'dots')[] = [];
    const start = Math.max(0, this.monitorCurrentPage - 2);
    const end = Math.min(totalPages - 1, this.monitorCurrentPage + 2);

    if (start > 0) {
      pages.push(0);
      if (start > 1) pages.push('dots');
    }

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    if (end < totalPages - 1) {
      if (end < totalPages - 2) pages.push('dots');
      pages.push(totalPages - 1);
    }

    return pages;
  }

  private buildColumns(): TableColumn[] {
    return [
      {
        key: 'sourceSystem',
        label: 'Source System',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'jurisdiction',
        label: 'Jurisdiction',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'ownerName',
        label: 'Owner',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'createdBy',
        label: 'Created By',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'createdOnDisplay',
        label: 'Created On',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'startedOnDisplay',
        label: 'Started On',
        hideMenu: true,
        headerType: 'text',
        cellType: 'text'
      },
      {
        key: 'actions',
        label: 'Actions',
        hideMenu: true,
        headerType: 'text',
        cellType: 'component',
        component: SettingsMigrateWorkflowActionCellComponent,
        componentInputs: row => ({ row }),
        componentOutputs: {
          action: (payload: { action: 'open'; row: { id: string } }) =>
            this.handleAction(payload)
        }
      }
    ];
  }

  private mapRow(processing: MigrationProcessing): MigrationProcessingRow {
    return {
      id: processing.id,
      sourceSystem: processing.sourceSystem || 'Untitled migration',
      jurisdiction: processing.jurisdiction || this.context.client?.name || '',
      ownerName: processing.currentOwner?.name || 'Unassigned',
      createdBy: processing.createdBy || 'Unknown',
      createdOnDisplay: this.formatDateTime(
        processing.createdOn,
        'Not available'
      ),
      startedOnDisplay: this.formatDateTime(
        processing.startedOn,
        'Not Started'
      ),
      raw: processing
    };
  }

  private mapMonitorRow(
    row: MigrationProcessingRow
  ): MigrationProcessingMonitorRow {
    const queueItems = this.taskQueueItemsByProcessingId.get(row.id) || [];
    const workflowTasks = queueItems.filter(
      item => item.taskType === 'migrate-workflow'
    );
    const applicationTasks = queueItems.filter(
      item => item.taskType === 'migrate-workflow-application'
    );
    const latestWorkflowTask = workflowTasks[0];
    const completedApplications = applicationTasks.filter(
      item => this.getTaskStatus(item) === 'Completed'
    ).length;
    const runningApplications = applicationTasks.filter(
      item => this.getTaskStatus(item) === 'Processing'
    ).length;
    const queuedApplications = applicationTasks.filter(
      item => this.getTaskStatus(item) === 'Queued'
    ).length;
    const failedApplications = applicationTasks.filter(
      item => this.getTaskStatus(item) === 'Error'
    ).length;
    const totalApplications = applicationTasks.length;
    const latestErrorItem = queueItems.find(item => !!item.errorMessage);

    return {
      id: row.id,
      name: row.sourceSystem,
      jurisdiction: row.jurisdiction,
      ownerName: row.ownerName,
      workflowStatus: latestWorkflowTask
        ? this.getTaskStatus(latestWorkflowTask)
        : row.raw.workflowMigratedOn
        ? 'Completed'
        : 'Not Started',
      applicationStatus: this.getApplicationStatus(row.raw, applicationTasks),
      totalApplications,
      completedApplications,
      runningApplications,
      queuedApplications,
      failedApplications,
      progressPercent:
        totalApplications > 0
          ? Math.round((completedApplications / totalApplications) * 100)
          : row.raw.applicationDataMigratedOn
          ? 100
          : 0,
      queuedOn: this.formatDateTime(latestWorkflowTask?.queuedOn, '-'),
      executedOn: this.formatDateTime(latestWorkflowTask?.executedOn, '-'),
      completedOn: this.formatDateTime(latestWorkflowTask?.completedOn, '-'),
      queuedDuration: latestWorkflowTask?.completedOn
        ? this.formatDuration(
            latestWorkflowTask.queuedOn,
            latestWorkflowTask.executedOn
          )
        : '-',
      executionDuration: latestWorkflowTask?.completedOn
        ? this.formatDuration(
            latestWorkflowTask.executedOn,
            latestWorkflowTask.completedOn
          )
        : '-',
      latestError: latestErrorItem?.errorMessage || ''
    };
  }

  private getApplicationStatus(
    processing: MigrationProcessing,
    applicationTasks: ScheduledTaskQueueItem[]
  ): MigrationTaskStatus {
    if (applicationTasks.some(item => this.getTaskStatus(item) === 'Error')) {
      return 'Error';
    }

    if (
      applicationTasks.some(item => this.getTaskStatus(item) === 'Processing')
    ) {
      return 'Processing';
    }

    if (applicationTasks.some(item => this.getTaskStatus(item) === 'Queued')) {
      return 'Queued';
    }

    if (applicationTasks.length > 0 || processing.applicationDataMigratedOn) {
      return 'Completed';
    }

    return 'Not Started';
  }

  private getTaskStatus(item: ScheduledTaskQueueItem): MigrationTaskStatus {
    if (item.errorMessage) {
      return 'Error';
    }

    if (item.completedOn) {
      return 'Completed';
    }

    if (item.executedOn) {
      return 'Processing';
    }

    return 'Queued';
  }

  private startMonitorPolling(): void {
    if (this.monitorPollingSub) {
      return;
    }

    this.monitorPollingSub = timer(10000, 10000).subscribe(() =>
      this.loadMonitorProcessings(false)
    );
  }

  private formatDateTime(
    value?: Date | string | null,
    emptyValue = 'Not Started'
  ): string {
    if (!value) {
      return emptyValue;
    }

    try {
      return formatDate(value, 'M/d/yy, h:mm a', 'en-US');
    } catch {
      return emptyValue;
    }
  }

  private formatDuration(
    start?: Date | string | null,
    end?: Date | string | null
  ): string {
    if (!start) {
      return '-';
    }

    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const totalSeconds = Math.max(
      0,
      Math.round((endTime - startTime) / 1000)
    );
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`
      : `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
